import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Icon from '../components/atoms/Icon';
import Button from '../components/atoms/Button';
import { getUploadStatus, uploadDocument, submitDocumentVerification } from '../services/api';

const DocumentUploadPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [uploadStatus, setUploadStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(10);

  // Available document types mapping
  const availableDocumentTypes = {
    'degree_certificate': { label: 'Degree Certificate', icon: 'school', description: 'University/College degree certificate' },
    '10th_certificate': { label: '10th Certificate', icon: 'grade', description: 'Secondary school certificate' },
    '12th_certificate': { label: '12th Certificate', icon: 'grade', description: 'Higher secondary certificate' },
    'experience_letter': { label: 'Experience Letter', icon: 'work', description: 'Work experience letter from employer' },
    'relieving_letter': { label: 'Relieving Letter', icon: 'exit_to_app', description: 'Relieving letter from employer' },
    'offer_letter': { label: 'Offer Letter', icon: 'description', description: 'Job offer letter from employer' },
    'pay_slip': { label: 'Pay Slip', icon: 'receipt', description: 'Salary slip from employer' },
    'bank_account': { label: 'Bank Account Statement', icon: 'account_balance', description: 'Bank account statement' },
    'pan_card': { label: 'PAN Card', icon: 'credit_card', description: 'Permanent Account Number card' },
    'aadhar_card': { label: 'Aadhar Card', icon: 'badge', description: 'Aadhar identification card' },
    'passport': { label: 'Passport', icon: 'flight', description: 'Passport for identity verification' },
    'driving_license': { label: 'Driving License', icon: 'directions_car', description: 'Driving license' },
    'other_certificate': { label: 'Other Certificate', icon: 'workspace_premium', description: 'Any other relevant certificate' }
  };

  // Get required document types from upload status (sent by HR)
  const getRequiredDocuments = () => {
    if (!uploadStatus?.required_documents || uploadStatus.required_documents.length === 0) {
      // Fallback to default if no specific requirements
      return ['degree_certificate', '10th_certificate', '12th_certificate', 'experience_letter'];
    }
    
    // Map HR selected labels back to keys
    const labelToKeyMap = {
      'Degree Certificate': 'degree_certificate',
      '10th Certificate': '10th_certificate',
      '12th Certificate': '12th_certificate',
      'Experience Letter': 'experience_letter',
      'Relieving Letter': 'relieving_letter',
      'Offer Letter': 'offer_letter',
      'Pay Slip': 'pay_slip',
      'Bank Account Statement': 'bank_account',
      'PAN Card': 'pan_card',
      'Aadhar Card': 'aadhar_card',
      'Passport': 'passport',
      'Driving License': 'driving_license',
      'Other Certificate': 'other_certificate'
    };
    
    return uploadStatus.required_documents.map(label => labelToKeyMap[label]).filter(Boolean);
  };

  const requiredDocumentKeys = getRequiredDocuments();
  const documentTypes = requiredDocumentKeys.map(key => ({
    key,
    ...availableDocumentTypes[key]
  })).filter(doc => doc.label); // Filter out any undefined document types

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    
    loadUploadStatus();
  }, [token, navigate]);

  const loadUploadStatus = async () => {
    try {
      setLoading(true);
      const status = await getUploadStatus(token);
      setUploadStatus(status);
      
      if (!status.valid) {
        // Token is invalid or expired
        return;
      }
    } catch (error) {
      console.error('Error loading upload status:', error);
      setUploadStatus({ valid: false, expired: true });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (documentType, file) => {
    if (!file) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'doc', 'docx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      alert('Please upload PDF, image files (JPG, PNG, GIF, WEBP), or Word documents (DOC, DOCX)');
      return;
    }
    
    setUploads(prev => ({
      ...prev,
      [documentType]: file
    }));
  };

  const handleUpload = async (documentType) => {
    const file = uploads[documentType];
    if (!file) return;
    
    try {
      setUploadProgress(prev => ({ ...prev, [documentType]: 'uploading' }));
      
      const result = await uploadDocument(token, documentType, file);
      
      if (result.success) {
        setUploadProgress(prev => ({ ...prev, [documentType]: 'success' }));
        // Reload status to update uploaded documents list
        await loadUploadStatus();
      } else {
        setUploadProgress(prev => ({ ...prev, [documentType]: 'error' }));
        alert(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(prev => ({ ...prev, [documentType]: 'error' }));
      alert('Upload failed. Please try again.');
    }
  };

  const getUploadStatusIcon = (documentType) => {
    const progress = uploadProgress[documentType];
    const isUploaded = uploadStatus?.document_types_uploaded?.includes(documentType);
    
    if (isUploaded) return { icon: 'check_circle', color: 'text-green-600' };
    if (progress === 'uploading') return { icon: 'hourglass_empty', color: 'text-blue-600' };
    if (progress === 'success') return { icon: 'check_circle', color: 'text-green-600' };
    if (progress === 'error') return { icon: 'error', color: 'text-red-600' };
    return { icon: 'upload', color: 'text-gray-400' };
  };

  const isDocumentUploaded = (documentType) => {
    return uploadStatus?.document_types_uploaded?.includes(documentType) || uploadProgress[documentType] === 'success';
  };

  const hasUploadedDocuments = uploadStatus?.documents_uploaded > 0;

  const handleSubmitDocuments = async () => {
    if (!hasUploadedDocuments) {
      alert('Please upload at least one document before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      const result = await submitDocumentVerification(token);
      
      if (result.success) {
        setSubmitted(true);
        // Start countdown for redirect
        const interval = setInterval(() => {
          setRedirectCountdown(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              navigate('/');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        alert(result.message || 'Failed to submit documents');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Failed to submit documents. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="max-w-2xl w-full mx-4">
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="check_circle" size={48} className="text-green-600" />
              </div>
              
              {/* Thank You Message */}
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Thank You!
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Your documents have been submitted successfully. Our HR team will review them and contact you soon.
              </p>
              
              {/* What's Next */}
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">What happens next?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                      <Icon name="smart_toy" size={24} className="text-blue-600" />
                    </div>
                    <p className="font-medium text-blue-900">AI Analysis</p>
                    <p className="text-blue-700 text-center">Documents analyzed using advanced AI</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                      <Icon name="person" size={24} className="text-blue-600" />
                    </div>
                    <p className="font-medium text-blue-900">HR Review</p>
                    <p className="text-blue-700 text-center">Manual verification by our team</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                      <Icon name="email" size={24} className="text-blue-600" />
                    </div>
                    <p className="font-medium text-blue-900">Notification</p>
                    <p className="text-blue-700 text-center">You'll receive an update via email</p>
                  </div>
                </div>
              </div>
              
              {/* Explore Aptirix */}
              <div className="space-y-4">
                <p className="text-gray-600">
                  While you wait, explore what Aptirix has to offer
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/')}
                    className="flex items-center justify-center gap-2"
                  >
                    <Icon name="explore" size={20} />
                    Explore Aptirix
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => navigate('/ai-trainer')}
                    className="flex items-center justify-center gap-2"
                  >
                    <Icon name="psychology" size={20} />
                    Try AI Trainer
                  </Button>
                </div>
              </div>
              
              {/* Auto Redirect */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Redirecting to homepage in <span className="font-semibold text-blue-600">{redirectCountdown}</span> seconds
                </p>
                <button 
                  onClick={() => navigate('/')}
                  className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                >
                  Go now
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!uploadStatus?.valid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <Icon name="error" size={64} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {uploadStatus?.expired ? 'Link Expired' : 'Invalid Link'}
          </h1>
          <p className="text-gray-600 mb-6">
            {uploadStatus?.expired 
              ? 'This document upload link has expired. Please contact HR for a new link.'
              : 'This document upload link is invalid or has already been used.'
            }
          </p>
          <Button onClick={() => navigate('/')}>
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon name="verified" size={32} className="text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Document Verification</h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Upload your documents securely for verification. Our AI-powered system ensures fast and accurate processing.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Candidate Welcome Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome, {uploadStatus.candidate_name}
              </h2>
              <p className="text-gray-600 mb-4">
                Please upload your documents below. We've made the process simple and secure.
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Icon name="schedule" size={16} />
                  <span>Expires: {new Date(uploadStatus.expires_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="security" size={16} />
                  <span>Secure Upload</span>
                </div>
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="text-4xl font-bold text-blue-600 mb-1">
                {uploadStatus.documents_uploaded}
              </div>
              <div className="text-sm text-gray-600 mb-4">Documents Uploaded</div>
              
              {/* Progress Circle */}
              <div className="relative w-20 h-20 mx-auto lg:mx-0">
                <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-blue-600"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={`${documentTypes.length > 0 ? (uploadStatus.documents_uploaded / documentTypes.length) * 100 : 0}, 100`}
                    strokeLinecap="round"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-semibold text-gray-700">
                    {documentTypes.length > 0 ? Math.round((uploadStatus.documents_uploaded / documentTypes.length) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Guidelines */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="info" size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-3">Upload Guidelines</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-blue-600" />
                  <span>Clear, readable documents only</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-blue-600" />
                  <span>PDF, JPG, PNG, Word formats</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-blue-600" />
                  <span>Maximum 10MB per file</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-blue-600" />
                  <span>Upload only available documents</span>
                </div>
              </div>
              <p className="text-blue-700 mt-3 text-sm">
                <strong>Note:</strong> You don't need to upload all document types. Upload only the documents you have available.
              </p>
            </div>
          </div>
        </div>

        {/* Document Upload Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {documentTypes.map((docType) => {
            const statusIcon = getUploadStatusIcon(docType.key);
            const isUploaded = isDocumentUploaded(docType.key);
            const isUploading = uploadProgress[docType.key] === 'uploading';
            const hasFile = uploads[docType.key];
            
            return (
              <div key={docType.key} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Icon name={docType.icon} size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{docType.label}</h3>
                      <p className="text-xs text-gray-500">{docType.description}</p>
                    </div>
                  </div>
                  <Icon name={statusIcon.icon} size={20} className={statusIcon.color} />
                </div>
                
                {isUploaded ? (
                  <div className="text-center py-6">
                    <Icon name="check_circle" size={32} className="text-green-600 mx-auto mb-2" />
                    <p className="text-green-700 font-medium text-sm">Uploaded Successfully</p>
                    <p className="text-xs text-gray-500 mt-1">Ready for verification</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <input
                        type="file"
                        id={`file-${docType.key}`}
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
                        onChange={(e) => handleFileSelect(docType.key, e.target.files[0])}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <label
                        htmlFor={`file-${docType.key}`}
                        className="block w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <Icon name="cloud_upload" size={24} className="text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          {hasFile ? hasFile.name : 'Click to select file'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Max 10MB</p>
                      </label>
                    </div>
                    
                    {hasFile && (
                      <Button
                        onClick={() => handleUpload(docType.key)}
                        disabled={isUploading}
                        className="w-full"
                        size="sm"
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Icon name="upload" size={16} className="mr-2" />
                            Upload
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Submit Section */}
        {hasUploadedDocuments && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="task_alt" size={32} className="text-green-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Submit?
              </h3>
              <p className="text-gray-600 mb-6">
                You've uploaded {uploadStatus.documents_uploaded} document{uploadStatus.documents_uploaded !== 1 ? 's' : ''}. 
                Click submit to send them for verification.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">After submission:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Documents will be analyzed using AI technology</p>
                  <p>• HR team will manually review each document</p>
                  <p>• You'll receive verification results via email</p>
                  <p>• The hiring process will continue based on verification</p>
                </div>
              </div>
              
              <Button
                onClick={handleSubmitDocuments}
                disabled={submitting}
                size="lg"
                className="px-8"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Submitting Documents...
                  </>
                ) : (
                  <>
                    <Icon name="send" size={20} className="mr-2" />
                    Submit Documents for Verification
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-500 mt-4">
                By submitting, you confirm that all uploaded documents are authentic and belong to you.
              </p>
            </div>
          </div>
        )}

        {/* No Documents Message */}
        {!hasUploadedDocuments && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
            <Icon name="upload_file" size={48} className="text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Upload Your Documents
            </h3>
            <p className="text-gray-600">
              Please upload at least one document to proceed with verification.
            </p>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default DocumentUploadPage;
